import { type ComponentProps, type ComponentType, useEffect, useState } from 'react';
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
  useRoomContext,
  ControlBar,
} from '@livekit/components-react';
import { RoomEvent, Track } from 'livekit-client';
import { getBreakoutToken, getViewerBreakoutState } from '../../api';
import { Button } from 'react-bootstrap';
import { DoorOpen, X } from 'lucide-react';
import { ViewerControlBar } from '../ViewerControlBar/ViewerControlBar';
import { getConfigOrThrow, notify, selectUser } from 'gossamer-front-end';
import { useSelector } from 'react-redux';
// import { ViewerControlBar } from '../ViewerControlBar/ViewerControlBar';

// Casts to avoid livekit/React JSX return type incompatibility (pre-existing project-wide issue)
const LiveKitRoomComponent = LiveKitRoom as unknown as ComponentType<ComponentProps<typeof LiveKitRoom>>;
const ParticipantTileComponent = ParticipantTile as unknown as ComponentType<ComponentProps<typeof ParticipantTile>>;

interface ViewerBreakoutRoomBannerProps {
  livestreamSessionId: string;
}

function BreakoutRoomView({ roomName, onLeave }: { roomName: string; onLeave: () => void }) {
  const room = useRoomContext();
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }]);

  useEffect(() => {
    if (!room) {
      return;
    }

    const handleDataReceived = (payload: Uint8Array<ArrayBufferLike>) => {
      try {
        const parsed = JSON.parse(new TextDecoder().decode(payload)) as { type?: string; message?: string };
        if (parsed.type === 'BREAKOUT_BROADCAST' && parsed.message?.trim()) {
          notify([parsed.message.trim(), 'info']);
        }
      } catch {
        // Ignore unrelated or malformed data packets.
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 bg-[#1e1e1e] shrink-0">
        <span className="text-sm font-semibold text-white">{roomName}</span>
        <Button className="bg-red-900 hover:bg-red-800 h-7 px-2 text-xs" onClick={onLeave}>
          <X className="w-3 h-3 mr-1" />
          Leave room
        </Button>
      </div>
      <div className="flex-1 overflow-hidden">
        <GridLayout tracks={tracks}>
          <ParticipantTileComponent />
        </GridLayout>
        <div className="w-full bg-red-900 absolute bottom-0 left-1/2 -translate-x-1/2">
          <ViewerControlBar />
        </div>
      </div>
      <RoomAudioRenderer />
    </div>
  );
}

export const ViewerBreakoutRoomBanner = ({ livestreamSessionId }: ViewerBreakoutRoomBannerProps) => {
  const user = useSelector(selectUser);
  const [breakoutToken, setBreakoutToken] = useState<string | null>(null);
  const [breakoutRoomName, setBreakoutRoomName] = useState<string>('Breakout Room');
  const [isJoining, setIsJoining] = useState(false);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [joinedAssignmentId, setJoinedAssignmentId] = useState<string | null>(null);
  const [joinedBreakoutRoomId, setJoinedBreakoutRoomId] = useState<string | null>(null);
  const [viewerState, setViewerState] = useState<{
    openRooms: { id: string; name: string; status: string }[];
    assignment: { id: string; breakoutRoomId: string } | null;
  }>({ openRooms: [], assignment: null });
  const [isRefreshingViewerState, setIsRefreshingViewerState] = useState(false);

  const assignment = viewerState.assignment;
  const assignedRoom = viewerState.openRooms.find((room) => room.id === assignment?.breakoutRoomId);
  const joinedRoomIsOpen = viewerState.openRooms.some((room) => room.id === joinedBreakoutRoomId);

  useEffect(() => {
    if (!user?.userId) {
      setViewerState({ openRooms: [], assignment: null });
      return;
    }

    let cancelled = false;

    const loadViewerState = async () => {
      setIsRefreshingViewerState(true);
      try {
        const response = await getViewerBreakoutState({ livestreamSessionId });
        if (!cancelled && response.result) {
          setViewerState({
            openRooms: response.result.openRooms,
            assignment: response.result.assignment,
          });
        }
      } finally {
        if (!cancelled) {
          setIsRefreshingViewerState(false);
        }
      }
    };

    void loadViewerState();
    const intervalId = window.setInterval(loadViewerState, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [livestreamSessionId, user?.userId]);

  useEffect(() => {
    if (!assignment?.id || assignment.id === dismissed) {
      return;
    }

    setDismissed((currentDismissed) => (currentDismissed === assignment.id ? currentDismissed : null));
  }, [assignment?.id, dismissed]);

  useEffect(() => {
    if (!breakoutToken || !joinedBreakoutRoomId) {
      return;
    }

    if (isRefreshingViewerState) {
      return;
    }

    const nextAssignedRoomId = assignment?.breakoutRoomId;

    if (nextAssignedRoomId === joinedBreakoutRoomId) {
      return;
    }

    if (!nextAssignedRoomId && joinedRoomIsOpen) {
      return;
    }

    setBreakoutToken(null);
    setJoinedAssignmentId(null);
    setJoinedBreakoutRoomId(null);

    if (assignment && assignedRoom) {
      notify([`You've been reassigned to ${assignedRoom.name}. Join when ready.`, 'info']);
      return;
    }

    notify([joinedRoomIsOpen ? 'Your breakout assignment changed.' : 'Your breakout room has closed.', 'info']);
  }, [assignedRoom, assignment, breakoutToken, isRefreshingViewerState, joinedBreakoutRoomId, joinedRoomIsOpen]);

  const handleJoin = async () => {
    if (!assignment) return;
    setIsJoining(true);
    try {
      const response = await getBreakoutToken({ breakoutRoomId: assignment.breakoutRoomId });
      if (response.result?.token) {
        setBreakoutToken(response.result.token);
        setBreakoutRoomName(assignedRoom?.name ?? 'Breakout Room');
        setJoinedAssignmentId(assignment.id);
        setJoinedBreakoutRoomId(assignment.breakoutRoomId);
      } else {
        notify(['Could not get breakout room access', 'danger']);
      }
    } catch {
      notify(['Failed to join breakout room', 'danger']);
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeave = () => {
    setBreakoutToken(null);
    setJoinedBreakoutRoomId(null);
    if (joinedAssignmentId) {
      setDismissed(joinedAssignmentId);
    }
    setJoinedAssignmentId(null);
  };

  // Active breakout room session overlay
  if (breakoutToken) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl h-[70vh] bg-[#111] rounded-xl overflow-hidden flex flex-col">
          <LiveKitRoomComponent
            token={breakoutToken}
            serverUrl={getConfigOrThrow('livekit-ws-url')}
            audio={false}
            video={false}
            onDisconnected={handleLeave}
            style={{ height: '100%' }}
          >
            <BreakoutRoomView roomName={breakoutRoomName} onLeave={handleLeave} />
          </LiveKitRoomComponent>
        </div>
      </div>
    );
  }

  if (!assignment || !assignedRoom || assignment.id === dismissed || assignedRoom?.status === 'CLOSED') {
    return null;
  }

  return (
    <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-[#1e1e1e] border border-blue-500 rounded-xl px-4 py-3 shadow-xl">
      <DoorOpen className="w-5 h-5 text-blue-400 shrink-0" />
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-white">
          You've been assigned to {assignedRoom?.name ?? 'a breakout room'}
        </span>
        <span className="text-xs text-gray-400">Join to participate in a smaller group discussion</span>
      </div>
      <div className="flex gap-2 ml-2">
        <Button className="bg-blue-600 hover:bg-blue-700 h-8 px-3 text-sm" onClick={handleJoin} disabled={isJoining}>
          {isJoining ? 'Joining...' : 'Join Room'}
        </Button>
        <Button
          className="bg-transparent hover:bg-[#2b2b2b] h-8 px-2"
          onClick={() => setDismissed(assignment.id)}
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
